import { createSelector, createSlice } from '@reduxjs/toolkit';
import { globalApi } from '../services/globalApi';
import { regionApi, getLocalLanguage, updateLocalLanguage } from '../services';
import type { RootState } from './index';

interface IAuth {
  global: Record<string, any>;
  regions: Record<string, any>;
  session: Record<string, any>;
  configs: Record<string, any>;
  locale: string;
  rejectedRegions: Record<string, any>;
  needLoginRegions: Record<string, any>;
  productVersions: Record<string, any>[];
  hbTime: Date;
  retentions: Record<string, any>;
  debugKey: string;
  defaultRegion: Record<string, any>;
}

const initialState: IAuth = {
  configs: {},
  global: {},
  productVersions: [],
  regions: {},
  defaultRegion: {},
  rejectedRegions: {},
  needLoginRegions: {},
  session: {
    status: '' //EXPIRED, REVOKED
  },
  locale: 'enUS',
  retentions: {},
  hbTime: new Date(),
  debugKey: ''
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateAuth: (state, { payload }) => {
      if (payload.global) {
        state.global = { ...state.global, ...payload.global };
      }
      if (payload.region && payload.region.alias) {
        state.regions[payload.region.alias] = payload.region;
      }
    },
    updateSessionStatus: (state, { payload }) => {
      if (payload) {
        state.session.status = payload;
      }
    },
    updateNeedLoginRegions: (state, { payload }) => {
      if (Object.keys(payload).length) {
        state.needLoginRegions = payload;
      }
    },
    updateHbTime: (state, { payload }) => {
      state.hbTime = payload;
      console.info(
        'extend region session and update hb time to:',
        state.hbTime
      );
    },
    updateLocale: (state, { payload }) => {
      const { key, locale } = payload as Record<string, string>;
      if (locale) {
        state.locale = locale;
      }
      if (key) {
        updateLocalLanguage(key);
      }
    },
    updateDebugKey: (state, { payload }) => {
      if (payload) {
        state.debugKey = payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      globalApi.endpoints.userLogin.matchFulfilled,
      (state, { payload, meta }) => {
        state.configs = {
          ...state.configs,
          server: meta?.arg?.originalArgs?.server || state.configs.server || ''
        };
        state.global = payload;
        state.session.status = 'auth';
        if (payload.group_region?.length) {
          const regions = payload.group_region;
          regions.forEach((obj) => {
            const alias = obj?.region_alias;
            if (alias) {
              state.regions[alias] = {
                ...obj,
                alias,
                id: obj?.region_id,
                name: obj?.region_alias,
                url: obj?.region_url,
                globalToken: payload.region_token,
                isHide: true
              };
            }
          });
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getSystemData.matchFulfilled,
      (state, { payload }) => {
        state.global.system = payload;
        if (state.global.system?.i18n_support) {
          state.locale = getLocalLanguage() || 'auto';
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getProductVersions.matchFulfilled,
      (state, { payload }) => {
        if (payload.length > 0) {
          state.productVersions = payload as Record<string, string>[];
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getRegions.matchFulfilled,
      (state, { payload }) => {
        payload.forEach((obj) => {
          const alias = obj?.alias;
          if (alias) {
            state.regions[alias] = {
              ...obj,
              globalToken: state.global.region_token,
              isHide: false
            };
            if (obj?.is_default) {
              state.defaultRegion = { ...obj };
            }
          }
        });
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getRetentions.matchFulfilled,
      (state, { payload }) => {
        if (Array.isArray(payload) && payload.length > 0) {
          const retentions = payload.reduce((acc, obj) => {
            const oldData = acc[obj.region_alias] || [];
            return {
              ...acc,
              [obj.region_alias]: [
                ...oldData,
                {
                  uuid: obj.id,
                  title: 'Instance Retention Needed',
                  type: 'retention',
                  region:
                    state.regions[obj.region_alias]?.name || obj.region_alias,
                  content:
                    'Your instance is expired, and will be deleted in two weeks. Please complete the Retention Form to extend your instance',
                  detail: obj
                }
              ]
            };
          }, {});
          state.retentions = retentions;
        }
      }
    );
    builder.addMatcher(
      regionApi.endpoints.regionLogin.matchFulfilled,
      (state, { payload }) => {
        const region: Record<string, any> = payload || {};
        // regions are keyed by alias; the region login response may use
        // `region` instead of `alias`, so fall back to it
        const alias = region?.alias || region?.region;
        if (alias) {
          state.regions[alias] = {
            ...state.regions[alias],
            ...region
          };
        }
      }
    );
    builder.addMatcher(
      regionApi.endpoints.regionLogin.matchRejected,
      (state, { error, meta }) => {
        console.warn('----------- region login rejected', meta);
        // const alias = meta?.arg?.originalArgs?.region?.alias || '';
        // if (alias) {
        //   state.rejectedRegions[alias] = meta?.arg?.originalArgs?.region;
        // }
      }
    );
  }
});

const { reducer, actions } = authSlice;

export const {
  updateAuth,
  updateHbTime,
  updateSessionStatus,
  updateNeedLoginRegions,
  updateLocale,
  updateDebugKey
} = actions;
export const selectAuth = (state: RootState) => state.auth;
export const selectAllowProvisionRegions = (state: RootState) =>
  state.auth.global.allow_provision_regions;
export const selectIsInternal = (state: RootState) =>
  state.auth.global.is_internal_user;
export const selectCurrentLocale = (state: RootState) => state.auth.locale;
export const selectSessionStatus = (state: RootState) =>
  state.auth.session.status;
export const selectRegions = (state: RootState) => state.auth.regions;
export const selectDefaultRegion = (state: RootState) =>
  state.auth.defaultRegion;
export const selectRejectedRegions = (state: RootState) =>
  state.auth.rejectedRegions;
export const selectProductVersions = (state: RootState) =>
  state.auth.productVersions;
export const selectRetentions = (state: RootState) => state.auth.retentions;
export const selectDebugKey = (state: RootState) => state.auth.debugKey;
export const selectHaEditMasterOnly = (state: RootState) =>
  state.auth.global.cluster_edit_master_only;
export const selectGlobalToken = (state: RootState) =>
  state.auth.global.region_token || '';
export const selectNotices = createSelector([selectRegions], (regions) => {
  const data = Object.keys(regions).reduce(
    (acc: Record<string, Record<string, any>[]>, key: string) => {
      return regions[key].notice?.length
        ? {
            ...acc,
            [key]: regions[key].notice
              .filter((obj: Record<string, any>) => obj.uuid)
              .map((obj: Record<string, any>) => ({
                ...obj,
                region: regions[key].name
              }))
          }
        : acc;
    },
    {}
  );
  return data;
});
export default reducer;
